/**
 * Sunbarry back-in-stock capture (PDP).
 *
 * Markup: snippets/buy-buttons.liquid, rendered INSIDE <buy-buttons>.
 * theme.js replaces that element wholesale on every variant change, so every
 * listener here is delegated on `document` -- anything bound directly to the
 * button or input would be destroyed on the first colour switch.
 *
 * Posts one event straight to a.klaviyo.com. Deliberately does NOT depend on
 * klaviyo.js (static.klaviyo.com has a history of failing to load on this store).
 *
 * No marketing consent is granted and no list is joined: the profile stays
 * NEVER_SUBSCRIBED, which Klaviyo still delivers campaign email to. Consent is
 * scoped to this one message and logged on the event itself.
 */
(function () {
  'use strict';

  var COMPANY_ID = 'S7QNky';
  var REVISION = '2024-10-15'; // matches tools/klaviyo-signups.js
  var METRIC = 'Back in Stock Requested';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
  var MIN_DWELL_MS = 1200; // a submit faster than this is not a human
  var MAX_SUBMITS = 5; // per page view

  var lastEmail = '';
  var done = {}; // "market|variantId|email" -> true
  var submits = 0;
  var mountedAt = Date.now();

  function attr(root, name) {
    return root.getAttribute('data-' + name) || '';
  }

  function setStatus(root, kind, text) {
    var el = root.querySelector('.sb-bis__status');
    if (el) {
      el.textContent = text || '';
      el.hidden = !text;
    }
    if (kind) {
      root.setAttribute('data-result', kind);
    } else {
      root.removeAttribute('data-result');
    }
  }

  function closest(el, selector) {
    return el && el.closest ? el.closest(selector) : null;
  }

  function submit(root) {
    if (!root || attr(root, 'state') !== 'ready') return;
    if (root.getAttribute('data-result') === 'pending') return;

    var input = root.querySelector('[data-sb-bis-email]');
    var honeypot = root.querySelector('[data-sb-bis-hp]');
    var email = ((input && input.value) || '').trim();

    // Bots: look successful, send nothing.
    if (honeypot && honeypot.value) {
      setStatus(root, 'ok', attr(root, 'msg-success'));
      return;
    }
    if (Date.now() - mountedAt < MIN_DWELL_MS) return;
    if (submits >= MAX_SUBMITS) return;

    if (!EMAIL_RE.test(email) || email.length > 120) {
      setStatus(root, 'err', attr(root, 'msg-invalid'));
      if (input) input.focus();
      return;
    }

    var market = attr(root, 'market');
    var variantId = attr(root, 'variant-id');
    var key = market + '|' + variantId + '|' + email.toLowerCase();
    if (done[key]) {
      setStatus(root, 'ok', attr(root, 'msg-duplicate'));
      return;
    }

    submits++;
    setStatus(root, 'pending', attr(root, 'msg-sending'));

    var now = new Date();
    var monthBucket = now.getUTCFullYear() + '-' + ('0' + (now.getUTCMonth() + 1)).slice(-2);

    fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(COMPANY_ID), {
      method: 'POST',
      mode: 'cors',
      keepalive: true,
      headers: { 'content-type': 'application/json', revision: REVISION },
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            metric: { data: { type: 'metric', attributes: { name: METRIC } } },
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  properties: {
                    sb_bis_language: attr(root, 'lang'),
                    sb_bis_market: market,
                    sb_bis_last_product: attr(root, 'product-title'),
                    sb_bis_last_variant: attr(root, 'variant-title')
                  }
                }
              }
            },
            // Dedupe per profile+metric, bucketed by month, so a fresh request
            // after a restock is still recorded rather than swallowed forever.
            unique_id: 'bis:' + market + ':' + variantId + ':' + monthBucket,
            properties: {
              variant_id: variantId,
              variant_title: attr(root, 'variant-title'),
              sku: attr(root, 'sku'),
              product_id: attr(root, 'product-id'),
              product_handle: attr(root, 'product-handle'),
              product_title: attr(root, 'product-title'),
              product_url: attr(root, 'product-url'),
              variant_url: attr(root, 'variant-url'),
              market: market,
              host: attr(root, 'host'),
              language: attr(root, 'lang'),
              source: 'pdp_inline_form',
              consent_text: attr(root, 'consent-text'),
              consented_at: now.toISOString()
            }
          }
        }
      })
    })
      .then(function (res) {
        if (res.status === 202 || res.ok) {
          done[key] = true;
          setStatus(root, 'ok', attr(root, 'msg-success'));
          if (input) input.value = '';
          lastEmail = '';
          return;
        }
        setStatus(root, 'err', attr(root, 'msg-error'));
        res
          .json()
          .then(function (body) {
            console.warn('[sb-bis] Klaviyo rejected the request', res.status, body);
          })
          .catch(function () {
            console.warn('[sb-bis] Klaviyo rejected the request', res.status);
          });
      })
      .catch(function (err) {
        setStatus(root, 'err', attr(root, 'msg-error'));
        console.warn('[sb-bis] network error', err);
      });
  }

  document.addEventListener('click', function (e) {
    var btn = closest(e.target, '[data-sb-bis-submit]');
    if (!btn) return;
    e.preventDefault();
    submit(closest(btn, '[data-sb-bis]'));
  });

  // Enter must subscribe, and must never reach the surrounding product form.
  document.addEventListener(
    'keydown',
    function (e) {
      if (e.key !== 'Enter') return;
      var input = closest(e.target, '[data-sb-bis-email]');
      if (!input) return;
      e.preventDefault();
      e.stopPropagation();
      submit(closest(input, '[data-sb-bis]'));
    },
    true
  );

  document.addEventListener('input', function (e) {
    var input = closest(e.target, '[data-sb-bis-email]');
    if (input) lastEmail = input.value;
  });

  // The block is replaced server-side on variant change: restore what was typed,
  // and re-show the confirmation if this address already signed up for that variant.
  function rehydrate() {
    var roots = document.querySelectorAll('[data-sb-bis]');
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      var input = root.querySelector('[data-sb-bis-email]');
      if (input && !input.value && lastEmail) input.value = lastEmail;
      if (!lastEmail) continue;
      var key = attr(root, 'market') + '|' + attr(root, 'variant-id') + '|' + lastEmail.toLowerCase();
      if (done[key]) setStatus(root, 'ok', attr(root, 'msg-duplicate'));
    }
  }

  // variant:change bubbles from the product form (theme.js). product:rerender does not.
  document.addEventListener(
    'variant:change',
    function () {
      setTimeout(rehydrate, 0);
    },
    true
  );
})();
